import { useEffect, useRef, useState } from 'react';
import * as T from 'three';
import { isLondonNight, type Interior } from '@london-life/contracts';
import type { Character } from '@london-life/game/game';
import { staffCharacter } from '@london-life/game/appearance';
import { createStage } from '@london-life/game/scene/stage';
import { buildVenue } from '@london-life/game/venue/scene';
import { VenueCrowd } from '@london-life/game/venue/crowd';
import { findPath, walkGrid } from '@london-life/game/venue/venues';
import { roomSky } from '../house/house-view';
import { offerSnapshot } from '../lib/camera';

export type VenuePerson = { id: string; character: Character; tag: string };
type Props = {
  placeId: string;
  // what the place looks like inside (published by the admin)
  interior: Interior;
  me: Character;
  // your @name, shown over your head like everyone else's
  myTag: string;
  people: VenuePerson[];
  // where your character should be (the spot of what you are doing), and whether it is busy there
  spot: { x: number; z: number } | null;
  busy: boolean;
  // what people just said, by person id ('me' for you)
  bubbles: Record<string, string>;
  onTag: (id: string) => void;
  // a tap on the floor somewhere nobody can walk to
  onBlocked?: () => void;
  dataSaver?: boolean;
  // day or night (London's clock when not given)
  night?: boolean;
};
const ME = 'me';
// how far up the screen the room sits (the bar covers the bottom)
const LIFT = 0.22;
// how far from a tapped spot the walk may end (a tap on a bar counter stops in front of it)
const REACH = 2.5;

// The inside of a place in 3D, the way Lagos Life shows one: the room with its own people (staff and visitors
// in their poses), you, and the players here walking about, everyone with their @name over their head (tap one
// to see them). Tap the floor to walk there. What someone says shows in a bubble above them for a few seconds.
// After 7 pm (London time) the sky is dark and the place's lamps light it.
export function VenueView({
  placeId,
  interior,
  me,
  myTag,
  people,
  spot,
  busy,
  bubbles,
  onTag,
  onBlocked,
  dataSaver = false,
  night: nightGiven,
}: Props) {
  const container = useRef<HTMLDivElement>(null);
  const overlay = useRef(new Map<string, HTMLElement>());
  const engine = useRef<{ crowd: VenueCrowd; stage: ReturnType<typeof createStage> } | null>(null);
  const [mode, setMode] = useState<'3d' | 'still' | 'loading'>('loading');
  const [ready, setReady] = useState(false);
  // one of the staff, tapped: their job shows over them for a few seconds
  const [staff, setStaff] = useState<{ id: string; role: string } | null>(null);
  useEffect(() => {
    if (!staff) return;
    const timer = window.setTimeout(() => setStaff(null), 3500);
    return () => window.clearTimeout(timer);
  }, [staff]);
  const latest = useRef({ me, dataSaver, busy, onBlocked });
  latest.current = { me, dataSaver, busy, onBlocked };
  useEffect(() => {
    const element = container.current;
    if (!element) return;
    const venue = interior;
    const grid = walkGrid(venue);
    const crowd = new VenueCrowd(grid);
    const night = nightGiven ?? isLondonNight();
    // a club is dark inside whatever the time
    const dark = night || venue.lights === 'party';
    let started = false;
    let clock = 0;
    let room: ReturnType<typeof buildVenue> | null = null;
    const stage = createStage(element, {
      sky: roomSky(dark),
      night: dark,
      dataSaver: latest.current.dataSaver,
      ready: () => started,
      fps: 30,
      tick: (dt) => {
        clock += dt;
        room?.tick(clock);
        crowd.update(dt);
        // name tags and bubbles follow the heads
        for (const [key, tag] of overlay.current) {
          const id = key.slice(key.indexOf(':') + 1);
          const at = crowd.headOnScreen(id, stage.camera, stage.size.width, stage.size.height);
          if (!at) {
            tag.style.visibility = 'hidden';
            continue;
          }
          tag.style.visibility = 'visible';
          // kept on the screen: half a tag in from either side
          const half = tag.offsetWidth / 2 + 4;
          const x = Math.min(Math.max(at.x, half), stage.size.width - half);
          tag.style.transform = `translate(${Math.round(x)}px, ${Math.round(at.y)}px) translate(-50%, -100%)`;
        }
      },
    });
    setMode(stage.mode);
    room = buildVenue(venue, {
      rich: !stage.slow,
      models: stage.mode === '3d' && !stage.slow && !latest.current.dataSaver,
      night,
    });
    stage.scene.add(room.group, crowd.group);
    stage.fit(venue.floor, LIFT);
    // the place's staff: the same faces every visit (a device drawing without a graphics chip gets three)
    (venue.crowd ?? [])
      .slice(0, stage.slow ? 3 : undefined)
      .forEach((member, i) => crowd.addMember(`npc:${i}`, staffCharacter(placeId, i, member.outfit), member));
    crowd.add(ME, latest.current.me, venue.entrance, false);

    // a tap (not a turn of the room) on the floor: walk there, round the things in the way
    const { canvas, camera } = stage;
    const raycaster = new T.Raycaster();
    const floorPlane = new T.Plane(new T.Vector3(0, 1, 0), 0);
    const pointer = new T.Vector2();
    const hit = new T.Vector3();
    let press: { x: number; y: number; id: number } | null = null;
    const down = (event: PointerEvent) => {
      if (event.isPrimary) press = { x: event.clientX, y: event.clientY, id: event.pointerId };
    };
    const up = (event: PointerEvent) => {
      if (!press || event.pointerId !== press.id) return;
      const tap = Math.hypot(event.clientX - press.x, event.clientY - press.y) < 8;
      press = null;
      if (!tap) return;
      const rect = canvas.getBoundingClientRect();
      pointer.set(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycaster.setFromCamera(pointer, camera);
      // a tap on one of the place's staff says who they are
      const member = crowd.memberAt(raycaster);
      if (member) {
        setStaff(member);
        return;
      }
      if (latest.current.busy) return;
      if (!raycaster.ray.intersectPlane(floorPlane, hit)) return;
      const to = { x: hit.x, z: hit.z };
      const from = crowd.at(ME);
      if (!from || Math.abs(to.x) > venue.floor.w / 2 || Math.abs(to.z) > venue.floor.d / 2) return;
      // a tap on a table or a stall walks up to it; only somewhere nobody can get near says so
      const end = findPath(grid, from, to).at(-1);
      if (!end || Math.hypot(end.x - to.x, end.z - to.z) > REACH) {
        if (Math.hypot(from.x - to.x, from.z - to.z) > 0.5) latest.current.onBlocked?.();
        return;
      }
      crowd.walkTo(ME, end);
    };
    canvas.addEventListener('pointerdown', down);
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', up);

    started = true;
    stage.requestStill();
    engine.current = { crowd, stage };
    setReady(true);
    // the in-game camera can take the place as it is
    const withdraw = offerSnapshot(() => stage.snapshot());
    return () => {
      withdraw();
      engine.current = null;
      setReady(false);
      canvas.removeEventListener('pointerdown', down);
      canvas.removeEventListener('pointerup', up);
      canvas.removeEventListener('pointercancel', up);
      crowd.dispose();
      room?.dispose();
      stage.dispose();
    };
  }, [placeId, interior, nightGiven]);
  // the players here come and go
  const peopleKey = people.map((person) => person.id).join(',');
  useEffect(() => {
    const crowd = engine.current?.crowd;
    if (!crowd || !ready) return;
    const wanted = new Map(people.map((person) => [person.id, person]));
    for (const id of crowd.ids()) if (id !== ME && !id.startsWith('npc:') && !wanted.has(id)) crowd.remove(id);
    const venue = interior;
    // those already here stand about the room; a newcomer walks in from the door
    const arriving = crowd.ids().some((id) => id !== ME && !id.startsWith('npc:'));
    for (const person of people)
      if (!crowd.has(person.id)) {
        const at = arriving
          ? venue.entrance
          : { x: (Math.random() - 0.5) * venue.floor.w, z: (Math.random() - 0.5) * venue.floor.d };
        crowd.add(person.id, person.character, at, true);
      }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peopleKey, ready, placeId]);
  // your character goes to the spot of what you are doing, and does it there
  const spotKey = spot ? `${spot.x},${spot.z}` : '';
  useEffect(() => {
    const crowd = engine.current?.crowd;
    if (!crowd || !ready || !spot) return;
    let timer = 0;
    // a spot on a dance floor: the character dances there while it is busy
    const dancing = interior.props.some(
      (prop) =>
        prop.shape === 'dancefloor' &&
        Math.abs(spot.x - prop.x) <= (prop.turn % 2 ? prop.d : prop.w) / 2 &&
        Math.abs(spot.z - prop.z) <= (prop.turn % 2 ? prop.w : prop.d) / 2,
    );
    crowd.walkTo(ME, spot, () => {
      if (!busy) return;
      crowd.face(ME, spot);
      if (dancing) return crowd.pose(ME, 'dance');
      crowd.act(ME, 'interact');
      timer = window.setInterval(() => crowd.act(ME, 'interact'), 3200);
    });
    return () => {
      window.clearInterval(timer);
      if (dancing) crowd.pose(ME, null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spotKey, busy, ready, placeId]);
  useEffect(() => {
    engine.current?.stage.saver(dataSaver);
  }, [dataSaver]);
  const keep = (key: string) => (element: HTMLElement | null) => {
    if (element) overlay.current.set(key, element);
    else overlay.current.delete(key);
  };
  return (
    <div className="venue-view" data-renderer={mode}>
      <div className="venue-canvas" ref={container} aria-label={`Inside ${interior.sign?.text ?? 'the place'}`} />
      <div className="venue-overlay">
        {people.map((person) => (
          <button
            key={person.id}
            type="button"
            className="venue-tag"
            ref={keep(`tag:${person.id}`)}
            onClick={() => onTag(person.id)}
          >
            {person.tag}
            {bubbles[person.id] && <span className="venue-bubble">{bubbles[person.id]}</span>}
          </button>
        ))}
        {staff && (
          <span className="venue-tag staff" ref={keep(`staff:${staff.id}`)}>
            {staff.role}
            <small>Staff</small>
          </span>
        )}
        <span className="venue-tag mine" ref={keep(`me:${ME}`)}>
          {myTag}
          {bubbles[ME] && <span className="venue-bubble">{bubbles[ME]}</span>}
        </span>
      </div>
    </div>
  );
}
